import { emptyFragment } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import * as React from 'react';
import { Link } from '../../../../Resource/Types';

interface Showing {
  kind: 'showing';
}

interface Failed {
  kind: 'failed';
}

type LogoState = Showing | Failed;

interface Props {
  link: Maybe<Link>;
}

/**
 * The uploaded collection logo and the column that holds it.
 *
 * The column is driven by the link, but a link alone does not mean the asset
 * resolves - an image_asset row with no usable path yields a dead URL. Left
 * alone that renders a broken image inside a column that still reserves space,
 * squeezing the description beside it. Dropping the whole column on error
 * mirrors what HtmlImageView already does for images embedded in the
 * description itself, so both variants degrade the same way.
 *
 * The column is capped at a quarter of the header on wide screens and the
 * image at 12rem tall, so a large upload cannot push the description aside
 * (DVLP-7971: a client logo was taking half the page).
 */
function CollectionLogo({ link }: Props) {
  const [state, setState] = React.useState<LogoState>({ kind: 'showing' });
  const href = link.map(({ href }) => href);

  // A different collection deserves a fresh attempt.
  const key = href.getOrElseValue('');
  React.useEffect(() => setState({ kind: 'showing' }), [key]);

  switch (state.kind) {
    case 'failed':
      return emptyFragment();
    case 'showing':
      return href
        .map((src) => (
          <div className="mt-4 w-full max-w-xs overflow-hidden md:max-w-[25%] md:shrink-0 md:grow-0 md:basis-1/4">
            <img
              className="h-auto max-h-48 max-w-full object-contain"
              src={src}
              alt=""
              onError={() => setState({ kind: 'failed' })}
            />
          </div>
        ))
        .getOrElse(emptyFragment);
    default:
      return assertNever(state);
  }
}

export default CollectionLogo;
